import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Play } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import {
  CCH_PIPELINE_RUNS_QUERY_KEY,
  CCH_PIPELINE_TRIGGERS,
  CCH_TRIGGER_DESCRIPTION,
  CCH_TRIGGER_LABEL,
  CchPipelineConflictError,
  cchPipelineRunQueryKey,
  getCchPipelineRun,
  triggerCchPipeline,
  type CchPipelineRun,
  type CchPipelineTriggerKind,
} from "@/services/cchApi";

interface Props {
  onTriggered: (run: CchPipelineRun) => void;
}

export default function CchPipelineTriggerCard({ onTriggered }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [trigger, setTrigger] = useState<CchPipelineTriggerKind>("raw");
  const [conflict, setConflict] = useState<{
    runId: string | null;
    message: string;
  } | null>(null);

  const start = useMutation({
    mutationFn: () => triggerCchPipeline({ trigger }),
    onSuccess: (run) => {
      toast({
        title: `${CCH_TRIGGER_LABEL[trigger]} started`,
        description: `Run ${run.run_id} has been handed to Fabric.`,
      });
      queryClient.invalidateQueries({ queryKey: CCH_PIPELINE_RUNS_QUERY_KEY });
      onTriggered(run);
    },
    onError: (e) => {
      if (e instanceof CchPipelineConflictError) {
        setConflict({ runId: e.runId, message: e.message });
        return;
      }
      toast({
        variant: "destructive",
        title: "Couldn’t start the pipeline",
        description: (e as Error).message,
      });
    },
  });

  const openRunning = async () => {
    if (!conflict?.runId) {
      setConflict(null);
      return;
    }
    try {
      const run = await getCchPipelineRun(conflict.runId);
      queryClient.setQueryData(cchPipelineRunQueryKey(run.run_id), run);
      onTriggered(run);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Couldn’t open the running pipeline",
        description: (err as Error).message,
      });
    } finally {
      setConflict(null);
    }
  };

  const busy = start.isPending;

  return (
    <section className="space-y-5 rounded-lg border border-border p-5">
      <div className="space-y-1">
        <h3 className="text-sm font-medium text-foreground">Run the CCH pipeline</h3>
        <p className="text-xs text-muted-foreground">
          Starts the Fabric CCH pipeline directly. This view becomes the live
          progress while a load is running, and comes back when it finishes.
        </p>
      </div>

      <RadioGroup
        value={trigger}
        onValueChange={(value) => setTrigger(value as CchPipelineTriggerKind)}
        className="grid gap-3 sm:grid-cols-2"
        aria-label="CCH pipeline trigger"
      >
        {CCH_PIPELINE_TRIGGERS.map((option) => (
          <label
            key={option}
            htmlFor={`cch-trigger-${option}`}
            className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
              trigger === option
                ? "border-primary bg-primary/5"
                : "border-border hover:bg-accent/50"
            }`}
          >
            <RadioGroupItem
              id={`cch-trigger-${option}`}
              value={option}
              className="mt-0.5"
            />
            <span className="space-y-1">
              <span className="block text-sm font-medium text-foreground">
                {CCH_TRIGGER_LABEL[option]}
              </span>
              <span className="block text-xs text-muted-foreground">
                {CCH_TRIGGER_DESCRIPTION[option]}
              </span>
            </span>
          </label>
        ))}
      </RadioGroup>

      <div className="flex flex-wrap items-center justify-end gap-3">
        <Button
          onClick={() => start.mutate()}
          disabled={busy}
          className="gap-2"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          {busy ? "Starting…" : `Start ${CCH_TRIGGER_LABEL[trigger]}`}
        </Button>
      </div>

      <AlertDialog
        open={conflict !== null}
        onOpenChange={(open) => !open && setConflict(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Already in progress</AlertDialogTitle>
            <AlertDialogDescription>
              {conflict?.message ||
                "A CCH pipeline run of this trigger is already in progress. Wait for it to finish or cancel it."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Dismiss</AlertDialogCancel>
            {conflict?.runId && (
              <AlertDialogAction onClick={openRunning}>
                Open running run
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
